import {showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {useCallback, useEffect} from 'react';

import {isNil, mergeAll} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {PostSupplierOrderMaterialBasketItemRequest} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {warehouseRoutes} from '@dms/routes';
import {
  DmsDataGrid,
  catchUnhandledDataGridActions,
  logError,
  useWarehouseDataGridTreeFolder,
} from '@dms/shared';
import {FullscreenDataGridWrapper} from '@dms/warehouse/shared-components';

import {composePath, TestIdProps, useLatestValue} from 'shared/web';

import type {ActionCallback, ExternalFilters, QueryFilterObject} from 'features/eag-grid';

import {DEFAULT_TO_ORDER_QUANTITY} from '../constants/defaultToOrderQuantity';
import {MaterialWarehouseTabExternalFilters} from './MaterialWarehouseTabExternalFilters';

const SUPPLIER_ORDER_MATERIAL_EXTERNAL_FILTER_ID = 'supplier-order-material-external-filter';

interface MaterialWarehouseTabProps extends TestIdProps {
  orderId: string;
  warehouseId: string;
  onAddMaterialToBasket: (
    article: PostSupplierOrderMaterialBasketItemRequest['body']['article']
  ) => Promise<void>;
  onActiveFolderIdChange?: (activeFolderId?: string) => void;
  onExternalFilterChange: (filters: ExternalFilters) => void;
  hasPendingRequest: boolean;
}

const PREDEFINED_COLUMNS = ['articleId', 'warehouseId', 'toOrder'] as const;

type CastedRowData = {
  articleId: {
    value: string;
  };
  toOrder: {
    value: number;
  };
};

export function MaterialWarehouseTab(props: MaterialWarehouseTabProps) {
  const [folderTreeRef, dataGridModifier, activeFolderId] = useWarehouseDataGridTreeFolder();

  const hasPendingRequestRef = useLatestValue(props.hasPendingRequest);

  useEffect(() => {
    props.onActiveFolderIdChange?.(activeFolderId);
  }, [activeFolderId]);

  const queryModifier = useCallback(
    (filter: QueryFilterObject) => {
      const modifiedFilter = mergeAll([
        filter,
        {warehouseId: props.warehouseId, orderId: undefined},
      ]);

      return dataGridModifier ? dataGridModifier?.queryModifier(modifiedFilter) : modifiedFilter;
    },
    [props.warehouseId, dataGridModifier]
  );

  const warehouseActionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = async ({
    actionKey,
    refreshData,
    getColumnData,
    rowData,
    deselectAll,
  }) => {
    if (hasPendingRequestRef.current) {
      return showNotification.warning(
        i18n.t('entity.warehouse.labels.thereIsAnotherOngoingAction')
      );
    }

    await match(actionKey)
      .with('toBasket', async () => {
        const isBulkAction = isArray(rowData);

        if (isBulkAction) {
          const castedRowData = rowData as CastedRowData[];

          const articlePayload = castedRowData.map((row) => ({
            articleId: row.articleId.value,
            toOrderQuantity: DEFAULT_TO_ORDER_QUANTITY,
          }));

          return await props
            .onAddMaterialToBasket(articlePayload)
            .then(() => refreshData())
            .then(() => deselectAll());
        }

        const castedRowData = rowData as CastedRowData;

        const articlePayload = [
          {
            articleId: castedRowData.articleId.value,
            toOrderQuantity: DEFAULT_TO_ORDER_QUANTITY,
          },
        ];

        await props
          .onAddMaterialToBasket(articlePayload)
          .then(() => refreshData())
          .then(() => deselectAll());
      })
      .with('replenishment', async () => {
        const isBulkAction = isArray(rowData);

        if (isBulkAction) {
          const castedRowData = rowData as CastedRowData[];

          const articlePayload = castedRowData.map((row) => ({
            articleId: row.articleId.value,
            toOrderQuantity: row.toOrder.value,
          }));

          return await props
            .onAddMaterialToBasket(articlePayload)
            .then(() => refreshData())
            .then(() => deselectAll());
        } else {
          const castedRowData = rowData as CastedRowData;

          const articlePayload = [
            {
              articleId: castedRowData.articleId.value,
              toOrderQuantity: castedRowData.toOrder.value,
            },
          ];

          await props
            .onAddMaterialToBasket(articlePayload)
            .then(() => refreshData())
            .then(() => deselectAll());
        }
      })
      .with('articleDetail', () => {
        const articleId = getColumnData<string>('articleId')?.value;

        if (isNil(articleId)) {
          return logError('Article id is not defined');
        }

        const warehouseId = getColumnData<{id: string}>('warehouseId')?.value?.id;

        if (isNil(warehouseId)) {
          return logError('Warehouse id is not defined');
        }

        const routePath = composePath(warehouseRoutes.articleDetailOverview, {
          params: {warehouseId, id: articleId},
          isSearchParamsPreserved: false,
        });
        window.open(routePath, '_blank')?.focus();
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  return (
    <FullscreenDataGridWrapper
      externalFilterId={SUPPLIER_ORDER_MATERIAL_EXTERNAL_FILTER_ID}
      folderTreeRef={folderTreeRef}
      hideCardWrapper
      data-testid={props['data-testid']}
    >
      <DmsDataGrid
        // DG must be re-rendered on folder change to update the query modifier
        // eslint-disable-next-line no-restricted-syntax
        key={dataGridModifier?.key}
        gridCode="warehouse-supplier-order-article-cart"
        actionCallback={warehouseActionCallback}
        predefinedColumns={PREDEFINED_COLUMNS}
        externalFilterId={SUPPLIER_ORDER_MATERIAL_EXTERNAL_FILTER_ID}
        queryModifier={queryModifier}
        _useAsLastResort_definitionBehaviorOverrides={{filterMode: 'NATIVE'}}
        data-testid="warehouse-supplier-order-article-cart"
        filterComponent={MaterialWarehouseTabExternalFilters}
        onFilterChanged={props.onExternalFilterChange as (filters: unknown) => void}
      />
    </FullscreenDataGridWrapper>
  );
}
